import React, { ComponentProps, forwardRef } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { font } from '../theme/base/mixins';
import {
  StyledAnchorComponent,
  StyledComponent,
  StyledDivComponent,
  StyledLIComponent,
  StyledSpanComponent,
  StyledUListComponent
} from '../types';
import { FontIcon, FontIconSpan } from './FontIcon';
import { Column, Row } from './Grid';
import { Icon } from './Icon';
import { Anker } from './Link';
import { rgba } from './utils/rgba';

/**
 * props:
 * - main
 * - meta
 * - footer
 */

interface TriangleProps {
  showTriangle?: boolean;
}

const triangleCSS = css`
  position: absolute;
  top: 24px;
  left: 50%;
  width: 20px;
  height: 20px;
  transform: translateX(-50%) rotate(45deg);
  border-left: 1px solid ${props => rgba(props.theme.colors.grayMedium, 0.8)};
  border-top: 1px solid ${props => rgba(props.theme.colors.grayMedium, 0.8)};
  background-color: ${props => props.theme.colors.white};
  z-index: 200;
  display: ${({ showTriangle }: TriangleProps) =>
    showTriangle ? 'block' : 'none'};
`;

export const NavigationHeader: StyledDivComponent = styled.div`
  ${font(3, 3.4, 'normal')};
  margin-bottom: 20px;
  text-align: left;
  color: ${(props: ThemeProps<Theme>) => rgba(props.theme.colors.black, 0.8)};
`;

type NavigationWrapperProps = {
  width?: number;
  triangleRight?: number;
} & TriangleProps;

type NavigationWrapperPropsThemed = NavigationWrapperProps & ThemeProps<Theme>;

export const NavigationWrapper: StyledDivComponent<
  NavigationWrapperProps
> = styled.div`
  position: absolute;
  top: 35px;
  background-color: ${(props: NavigationWrapperPropsThemed) =>
    props.theme.colors.white};
  border: 1px solid ${props => rgba(props.theme.colors.grayMedium, 0.8)};

  ${({ width }: NavigationWrapperPropsThemed) =>
    width != null &&
    css`
      width: ${width}px;
    `};

  ${NavigationHeader} {
    padding: 20px 35px 0 35px;
  }

  &:after {
    content: ' ';
    ${triangleCSS};
    top: -11px;
    left: auto;
    right: ${(props: NavigationWrapperPropsThemed) =>
      props.triangleRight || 0}px;
  }
`;

export const NavigationWrapperClose: StyledSpanComponent = styled.span`
  position: absolute;
  right: 10px;
  top: 10px;
  display: block;
  cursor: pointer;

  ${Icon} {
    margin: 0 !important;
    width: 20px !important;
    height: 20px !important;

    svg {
      width: 20px !important;
      height: 20px !important;
      fill: ${(props: ThemeProps<Theme>) =>
        props.theme.colors.grayDark} !important;
    }
  }

  &:hover {
    ${Icon} {
      svg {
        fill: ${(props: ThemeProps<Theme>) =>
          props.theme.colors.primary} !important;
      }
    }
  }
`;

const navigationLinkActiveCss = css`
  color: ${(props: ThemeProps<Theme>) => props.theme.colors.secondary};

  :before {
    content: '';
    position: absolute;
    bottom: -25px;
    left: 0;
    right: 0;
    height: 30px;
  }

  :after {
    content: '';
    display: block;
    position: absolute;
    top: 25px;
    left: 50%;
    width: 20px;
    height: 20px;
    transform: translateX(-50%) rotate(45deg);
    border-left: 1px solid
      ${(props: ThemeProps<Theme>) => rgba(props.theme.colors.grayMedium, 0.8)};
    border-top: 1px solid
      ${(props: ThemeProps<Theme>) => rgba(props.theme.colors.grayMedium, 0.8)};
    background-color: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
    z-index: 9999;
  }
`;

type NavigationLinkProps = NavLinkProps & {
  disabled?: boolean;

  // @see https://reactrouter.com/web/api/NavLink for this and more props provided by react-router-dom
  active?: boolean;
};

const NavigationLink = (
  { active, ...props }: NavigationLinkProps,
  ref: any // FIXME
) => <NavLink {...props} {...active} innerRef={ref} />;

const StyledNavigationLink: StyledAnchorComponent<
  NavigationLinkProps & ComponentProps<typeof Anker>
> = Anker.withComponent(
  // needed to be done to get the reference to the underlying HTMLAnchorElement
  forwardRef(NavigationLink)
).extend`
  white-space: nowrap;
  padding-bottom: 2px;
  position: relative;

  ${(props: NavigationLinkProps) => props.active && navigationLinkActiveCss};
` as any; // FIXME

export const NavigationListItem: StyledLIComponent<TriangleProps> = styled.li`
  padding: 0;

  span {
    vertical-align: middle;
  }

  ${FontIcon}, ${FontIconSpan} {
    &:before {
      margin-right: 5px;
      font-size: 20px;
      vertical-align: bottom;
    }

    &[icon='sendMail'] {
      &:before {
        font-size: 32px;
      }
    }
  }

  ${Anker} {
    padding-bottom: 0;
  }

  ${Icon} {
    margin-right: 6px;

    canvas {
      height: 18px;
      width: auto;
    }
  }
`;

export const NavigationListItemWithTriangle = NavigationListItem.extend`
  > ${StyledNavigationLink} {
    :after {
      content: ' ';
      ${triangleCSS};
    }
  }
`;

interface NavigationListProps {
  inlined?: boolean;
}

export const NavigationList: StyledUListComponent<
  NavigationListProps
> = styled.ul`
  position: relative;

  li {
    ${(props: NavigationListProps) =>
      props.inlined
        ? css`
            display: inline-block;
          `
        : ''};
  }
`;

interface NavigationProps {
  type?: 'main' | 'footer' | 'meta' | 'language';
}

type NavigationPropsThemed = ThemeProps<Theme> & NavigationProps;

export const Navigation: StyledComponent<NavigationProps> = styled.nav`
  background-color: ${(props: NavigationPropsThemed) =>
    props.type === 'meta'
      ? props.theme.colors.grayBg
      : (
        props.type === 'footer'
          ? 'transparent'
          : props.theme.colors.white
      )};

  padding: ${(props: NavigationPropsThemed) => (props.type === 'meta' ? '18px 0' : '0')};

  // #48782 fix overlapping of menus
  ${(props: NavigationPropsThemed) =>
    props.type === 'meta'
      ? css`
          z-index: 5000;
          position: relative;
        `
      : ``}

  ${(props: NavigationPropsThemed) =>
    props.type === 'main'
      ? css`
          border-top: 1px solid ${props.theme.colors.gray};
          padding: ${props.theme.grid.columnGap}px 0;
        `
      : ``}

  li {
    ${(props: NavigationPropsThemed) =>
      props.type === 'meta' || props.type === 'language'
        ? ``
        : css`
            position: relative;
          `}
  }

  > ${Row} {
    > ${Column} {
        z-index: 1;
    }
  }

  ul {
    text-align: ${(props: NavigationPropsThemed) =>
      props.type === 'meta' || props.type === 'language' ? 'right' : 'left'};

  ${(props: NavigationPropsThemed) =>
    props.type === 'main'
      ? css`
          max-width: ${props.theme.grid.maxWidth}px;
          padding-left: ${props.theme.grid.columnGap}px;
          padding-right: ${props.theme.grid.columnGap}px;
          margin: 0 auto;

          ul {
            padding: 0;
          }
        `
      : ``}

  li {
    text-transform: ${(props: NavigationPropsThemed) =>
      props.type === 'main' ? 'uppercase' : 'none'};
    margin: ${(props: NavigationPropsThemed) =>
      props.type === 'footer' ? '0' : '0 0 0 40px'};

    ${(props: NavigationPropsThemed) =>
      props.type === 'main'
        ? css`
            &:first-child {
              margin-left: 0;
            }
          `
        : null}

    color: ${(props: NavigationPropsThemed) =>
      props.type === 'main' || props.type === 'language'
        ? props.theme.colors.black
        : (
          props.type === 'meta'
            ? props.theme.colors.grayMedium
            : props.theme.colors.white
        )};

    &:hover {
      > ${StyledNavigationLink} {
        color: ${(props: NavigationPropsThemed) =>
          props.type === 'main' || props.type === 'language'
            ? props.theme.colors.primary
            : (
              props.type === 'meta'
              ? props.theme.colors.secondary
              : ''
            )};
      }
    }

    ${StyledNavigationLink} {
      ${(props: NavigationPropsThemed) =>
        props.type === 'main'
          ? font(2.2, 2.5, 'bold')
          : (
            props.type === 'meta' || props.type === 'language'
              ? font(1.4, 1.6, '500')
              : font(1.6, 2.5, 'normal')
          )};

      color: ${(props: NavigationPropsThemed) =>
        props.type === 'main' || props.type === 'language'
          ? props.theme.colors.black
          : (
            props.type === 'meta'
              ? props.theme.colors.grayMedium
              : props.theme.colors.white
          )};

      letter-spacing: ${(props: NavigationPropsThemed) =>
        props.type === 'main' ? '1.22px' : 'normal'};

      :hover {
        color: ${(props: NavigationPropsThemed) =>
          props.type === 'meta' || props.type === 'language'
            ? props.theme.colors.secondary
            : props.theme.colors.primary};
      }

      &.active {
        color: ${(props: NavigationPropsThemed) => props.theme.colors.primary};

        svg {
          fill: ${(props: NavigationPropsThemed) => props.theme.colors.primary};
        }

        &:hover {
          svg {
            fill: ${(props: NavigationPropsThemed) =>
              props.theme.colors.primary};
          }
        }

        ${(props: NavigationPropsThemed) =>
          (props.type === 'main' || props.type === 'language') &&
          css`
            border-bottom: 2px solid ${props.theme.colors.primary};
          `};
      }

      &[disabled] {
        pointer-events: none;

        ${(props: NavigationPropsThemed) =>
          props.type === 'language'
            ? `color: ${props.theme.colors.grayMedium};`
            : ''
        }
      }
    }

    ul {
      display: none;
      position: absolute;
      left: -35px;
      top: 35px;
      background-color: ${(props: NavigationPropsThemed) =>
        props.theme.colors.white};
      border: 1px solid rgba(${(props: NavigationPropsThemed) =>
        props.theme.colors.grayMedium}, 0.8);
      text-align: left;
    }

    ${NavigationWrapper} {
      ${Column} {
        margin: 0;
      }

      > ${Row} {
        > ${Column} {
          flex: auto;
        }
      }

      ul {
        display: block;
        position: static;
        border: none;
        top: auto;
        left: auto;
      }

      ${(props: NavigationPropsThemed) =>
        (props.type === 'meta' || props.type === 'language') &&
        css`
          right: 0;
        `}
    }

    li {
      display: block;
      margin: 0;
      text-transform: none;

      ${StyledNavigationLink} {
        position: relative;
        display: inline-block;
        padding: 15px 35px;
        width: 100%;
        z-index: 250;
        letter-spacing: normal;

        :before {
          color: ${(props: NavigationPropsThemed) =>
            props.theme.colors.primary};
          font-size: 1.8rem;
        }

        :after {
          content: '';
          border-bottom: 1px solid ${(props: NavigationPropsThemed) =>
            props.theme.colors.grayLight};
          transform: translate(0);
          width: auto;
          height: 0;
          display: block;
          position: absolute;
          top: auto;
          bottom: 0;
          left: 20px;
          right: 20px;
        }

        :hover {
          background-color: ${(props: NavigationPropsThemed) =>
            props.theme.colors.gray};
          color: ${(props: NavigationPropsThemed) => props.theme.colors.black};

          &:after {
            border-bottom-color: ${(props: NavigationPropsThemed) =>
              props.theme.colors.gray};
          }
        }
      }

      ${StyledNavigationLink} {
        ${font(1.8, 2.1, 'normal')};
      }

      :last-child {
        ${StyledNavigationLink} {
          :after {
            display: none;
          }
        }
      }
    }
  }
`;

export { StyledNavigationLink as NavigationLink };
